/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import { FontWeights, Stack } from '@fluentui/react';
import { Property } from 'csstype';
import { parse } from 'marked';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisText } from 'src/core/components/LuisText';
import { LuisTextField } from 'src/core/components/LuisTextField';
import { useFormState } from 'src/core/hooks/useFormState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useResponsiveMode } from 'src/core/hooks/useResponsiveMode';
import { FormValidators } from 'src/core/utils/formValidators';
import { AMLConnectionInfo } from 'src/customText/core/stores/CustomTextProjectStore';
import { AMLConnectionSteps } from 'src/customText/verticals/azureMLConnectionModal/AzureMLConnectionModal';
import { AzureMLTabConnectionFooter } from 'src/customText/verticals/azureMLConnectionModal/components/AzureMLTabConnectionFooter';
import './ckStyles.css';

require('@ckeditor/ckeditor5-build-classic/build/ckstyles.css');

type InstructionPageProps = {
    onNext: (amlConnectionInfo: Partial<AMLConnectionInfo>) => void;
    onDismiss: () => void;
    onPrevious: () => void;
    amlConnectionInfo: AMLConnectionInfo;
};

const messages = defineMessages({
    title: {
        id: 'customText.instructionPage.title',
        defaultMessage: 'Labeling instructions (optional)'
    },
    subTitle: {
        id: 'customText.instructionPage.subTitle',
        defaultMessage:
            'Instructions for the human labelers will help them understand the task. Clear definitions of the labels, including examples, will provide the best results.'
    },
    textInputLabel: {
        id: 'customText.instructionPage.textInputLabel',
        defaultMessage: 'Detailed task instruction URL'
    },
    addUrlHere: {
        id: 'customText.instructionPage.addUrlHere',
        defaultMessage: 'Add URL here'
    },
    textFieldLabel: {
        id: 'customText.instructionPage.textFieldLabel',
        defaultMessage: 'Task instructions'
    },
    addInstructionHere: {
        id: 'customText.instructionPage.addInstructionHere',
        defaultMessage: 'Add instructions here'
    }
});

const parseHTML = (html: string) => {
    const div = document.createElement('div');
    div.innerHTML = html;
    const text = div.textContent;
    return text;
};

export const InstructionPage = (props: InstructionPageProps) => {
    const { onNext, onPrevious, onDismiss, amlConnectionInfo } = props;
    const { localize } = useLocalization();
    const { isLargeOrGreater } = useResponsiveMode();
    const { formState, updateFormState } = useFormState<Partial<AMLConnectionInfo>>({
        labelingInstructionText: [parseHTML(amlConnectionInfo?.labelingInstructionText) ?? '', FormValidators.createRequiredValidator()],
        labelingInstructionUrl: [amlConnectionInfo?.labelingInstructionUrl ?? '', FormValidators.createRequiredValidator()]
    });

    const handleNext = () =>
        onNext({
            labelingInstructionText: parse(formState.fields.labelingInstructionText.value),
            labelingInstructionUrl: formState.fields.labelingInstructionUrl.value
        });

    const handleRichEditorOnChange = (_: any, editor: any) => {
        const data = editor.getData();
        updateFormState({ labelingInstructionText: data });
    };

    return (
        <Stack styles={{ root: { minHeight: '600px' } }}>
            <Stack
                styles={{ root: { maxHeight: '500px', overflowY: 'auto', marginRight: '20px' } }}
                tokens={{ childrenGap: 12, padding: `${isLargeOrGreater ? `0 0 0 32px` : '12px 20px'}` }}>
                <LuisText variant="xLarge" fontWeight={FontWeights.semibold as Property.FontWeight}>
                    {localize(messages.title)}
                </LuisText>
                <LuisText>{localize(messages.subTitle)}</LuisText>
                <LuisTextField
                    value={formState.fields.labelingInstructionUrl.value}
                    label={localize(messages.textInputLabel)}
                    placeholder={localize(messages.addUrlHere)}
                    onChange={(_, val) => updateFormState({ labelingInstructionUrl: val })}
                />
                <Stack.Item onKeyDown={e => e.stopPropagation()}>
                    <CKEditor
                        editor={ClassicEditor}
                        config={{ outerHeight: 200 }}
                        onChange={handleRichEditorOnChange}
                        data={formState.fields.labelingInstructionText.value}
                        placeholder={localize(messages.addInstructionHere)}
                    />
                </Stack.Item>
            </Stack>
            <AzureMLTabConnectionFooter
                onNext={handleNext}
                onDismiss={onDismiss}
                onPrevious={onPrevious}
                activeStep={AMLConnectionSteps.Instructions}
            />
        </Stack>
    );
};
